import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  formatQuotedPrice,
  formatWeekdayDateOrDash,
} from "@shared-core/domain/formatters";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { extractFieldErrors } from "@shared-core/data/validation";
import { poFormatHint } from "@shared-core/domain/credit-control-presentation";
import { DELIVERY_NOT_YET_PRICED } from "@shared-core/domain/checkout-delivery";
import type {
  CustomerBankAccount,
  CustomerOrderProformaDue,
} from "@shared-core/data/api-types";
import type {
  ArtworkWrites,
  OrderNeedView,
  OrderPageView,
} from "@shared-core/data/customer-link-adapter";
import { Card } from "../../app/components/primitives/card";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { cn } from "../../app/components/primitives/utils";
import {
  CalendarClock,
  CheckCircle2,
  CreditCard,
  PackageCheck,
} from "lucide-react";
import { DeliveryByAddress } from "./DeliveryByAddress";
import { DocumentsList } from "./DocumentsList";
import type { LoadKitDrops } from "./KitPlaces";
import { ItemsListDetail } from "./ItemsListDetail";
import { NeedsYouList, type NeedsYouItem } from "./NeedsYouList";
import { NotesPanel } from "./NotesPanel";
import { OrderItemDetail } from "./OrderItemDetail";
import { OrderSummaryBar, OrderSummaryPanel } from "./OrderSummaryPanel";
import { PageTabs, resolvePageTab, type PageTab } from "./PageTabs";
import { PoNumberField } from "./PoNumberField";
import { PriceBreakdown } from "./PriceBreakdown";
import { SummaryPageFrame } from "./SummaryPageFrame";
import type { ItemPane } from "./ItemSubTabs";
import { useFileDownload, type FetchFile } from "./useFetchedFile";
import { SUMMARY_BESIDE_PAGE, useMediaQuery } from "./useMediaQuery";
import { useItemPageParams } from "./useItemPageParams";
import {
  usePortalPageSlots,
  type PortalPageSlots,
} from "../portal/PortalPageSlots";

// The customer's order page (order to invoice doc 39 §7, doc 43 §7, doc 47
// §5), in the pattern the quote page shares (Ben, 5 Oct): the header (the
// order, the customer, the total including VAT and whether it is paid, and
// the approve-by sentence); Needs you · N, every action one row each with
// why, each opening its spot (the page opens here whenever anything is
// outstanding, else on Items); Items · N, list and detail; Delivery, Price,
// Documents, Notes. Tab, item, sub-tab and an open proof live in the URL
// (`?tab=&item=&view=&proof=&focus=`), so the per-order email can deep-link.
// Every sentence is the read's; the body only places it. Paying is done in
// the summary beside the page (OrderSummaryPanel, a bar and sheet on a phone):
// Pay now, a pro-forma's bank details with the payment reference and Pay by
// card instead, and the PO number's one field. Their Needs you rows say what
// is owed and their button goes to the summary (paying first, the sales
// ledger, SL3); the Documents tab lists the pro-forma and, once paid, the receipt. A
// line staff cancelled is listed quietly after the live ones (Items, Price):
// greyed, "Cancelled on {day}", no price, in no total, never a reason (Ben,
// 7 Oct). No
// mock-up was drawn for these: they are built minimal, in the page's pattern.
// The body knows nothing of how it was reached: the link route and the
// signed-in portal both hand over what each action does (the read's URLs are
// the credential's: a token's, or the session's). A page that acts as nobody (the
// staff view-as mirror) hands over `actions` null: what the order asks of the
// customer is still listed, without the buttons and fields.

export interface OrderPageActions {
  /** Go to the card checkout for the order's payment, or an addition's. */
  pay: (addedLineRef: string | null) => Promise<void>;
  givePoNumber: (customerOrderNumber: string) => Promise<void>;
  reload: () => Promise<void>;
  writes: ArtworkWrites;
  /** Fetches a read's API-relative URL (a document, a proof's PDF, a page picture) with the session and chosen customer. */
  fetchFile: FetchFile;
}

/** Shown once, on arrival: the order just placed, or Stripe's return. */
export type OrderArrival = "placed" | "payment_return" | null;

const READ_ONLY = "This page is read-only.";

/** The files of a page with no actions: its read names no file URL, so none is fetched. */
const NO_FILES: FetchFile = () => Promise.reject(new Error(READ_ONLY));

/** The artwork writes of a page with no actions: a read-only read names no URL to write to, so none is reached. */
const READ_ONLY_WRITES: ArtworkWrites = {
  uploadFile: () => Promise.reject(new Error(READ_ONLY)),
  removeFile: () => Promise.reject(new Error(READ_ONLY)),
  nameVersion: () => Promise.reject(new Error(READ_ONLY)),
  markProofOpened: () => Promise.reject(new Error(READ_ONLY)),
  uploadWholeFile: () => Promise.reject(new Error(READ_ONLY)),
  splitCombinedFile: () => Promise.reject(new Error(READ_ONLY)),
  discardCombinedFile: () => Promise.reject(new Error(READ_ONLY)),
  decideProof: () => Promise.reject(new Error(READ_ONLY)),
};

function PayButton({
  addedLineRef,
  pay,
  label = "Pay now",
}: {
  addedLineRef: string | null;
  pay: OrderPageActions["pay"];
  label?: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Button
        size="sm"
        disabled={busy}
        onClick={async () => {
          setError(null);
          setBusy(true);
          try {
            await pay(addedLineRef);
          } catch (err) {
            setBusy(false);
            setError(
              mutationErrorMessage(err, "We couldn't start the card payment."),
            );
          }
        }}
      >
        <CreditCard className="size-4" />
        {label}
      </Button>
      {error && (
        <span role="alert" className="text-xs text-red-700 dark:text-red-400">
          {error}
        </span>
      )}
    </span>
  );
}

/** The pro-forma's PDF, fetched with the chosen customer (a plain link would be refused for a person with several). */
function ProformaDownload({
  url,
  proformaNo,
  fetchFile,
}: {
  url: string;
  proformaNo: string;
  fetchFile: FetchFile;
}) {
  const { isBusy, hasFailed, download } = useFileDownload(fetchFile);
  return (
    <>
      <button
        type="button"
        disabled={isBusy}
        aria-busy={isBusy}
        onClick={() => void download(url, `${proformaNo}.pdf`)}
        className="font-medium text-foreground underline underline-offset-2 disabled:opacity-50"
      >
        {isBusy ? "Preparing…" : `Download pro-forma ${proformaNo}`}
      </button>
      {hasFailed && (
        <span role="alert" className="block text-red-700 dark:text-red-400">
          We couldn&apos;t download that just now. Try again in a moment.
        </span>
      )}
    </>
  );
}

/** A pro-forma owed: how to pay it by bank transfer, quoting its number. */
function ProformaPayment({
  proforma,
  bank,
  fetchFile,
}: {
  proforma: CustomerOrderProformaDue;
  bank: CustomerBankAccount | null;
  fetchFile: OrderPageActions["fetchFile"] | null;
}) {
  return (
    <div className="space-y-2 rounded-md bg-muted/50 p-3 text-sm">
      {proforma.paid_pence > 0 && (
        <p>
          {formatQuotedPrice(proforma.paid_pence)} of{" "}
          {formatQuotedPrice(proforma.gross_pence)} received; the balance is{" "}
          {formatQuotedPrice(proforma.outstanding_pence)}.
        </p>
      )}
      {bank && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt className="text-muted-foreground">Account name</dt>
          <dd>{bank.account_name}</dd>
          <dt className="text-muted-foreground">Sort code</dt>
          <dd className="tabular-nums">{bank.sort_code}</dd>
          <dt className="text-muted-foreground">Account number</dt>
          <dd className="tabular-nums">{bank.account_number}</dd>
          <dt className="text-muted-foreground">Reference</dt>
          <dd className="font-medium">{proforma.payment_reference}</dd>
        </dl>
      )}
      <p className="text-xs text-muted-foreground">
        Valid until {formatWeekdayDateOrDash(proforma.valid_until)}
        {proforma.document_url && fetchFile && (
          <>
            {" · "}
            <ProformaDownload
              url={proforma.document_url}
              proformaNo={proforma.proforma_no}
              fetchFile={fetchFile}
            />
          </>
        )}
      </p>
    </div>
  );
}

/** The order's PO number field: given on its own, as orders takes it. */
function OrderPoNumber({
  formatHint,
  givePoNumber,
  reload,
}: {
  formatHint: string | null;
  givePoNumber: OrderPageActions["givePoNumber"];
  reload: () => Promise<void>;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <PoNumberField
      value={value}
      onChange={(v) => {
        setValue(v);
        if (error) setError(null);
      }}
      isRequired
      formatHint={formatHint}
      error={error}
      onSave={async () => {
        try {
          await givePoNumber(value);
          await reload();
        } catch (err) {
          const errors = extractFieldErrors(err);
          setError(
            (
              errors.find((e) => e.field === "customer_order_number") ??
              errors[0]
            )?.message ??
              mutationErrorMessage(err, "We couldn't save your PO number."),
          );
        }
      }}
    />
  );
}

/** The money part of the summary: what is to pay and how, the PO number, and the page's one sentence on payment. */
function MoneyActions({
  page,
  actions,
}: {
  page: OrderPageView;
  actions: OrderPageActions | null;
}) {
  const hasCardDue = page.payments_due.length > 0;
  return (
    <div className="space-y-4">
      {page.card_payments.map((p) => (
        <p
          key={`paid-${p.paid_at}`}
          className="text-sm text-muted-foreground tabular-nums"
        >
          Paid {formatQuotedPrice(p.amount_pence)} by card on{" "}
          {formatWeekdayDateOrDash(p.paid_at)}
          {p.for_sentence && ` for ${p.for_sentence}`}
        </p>
      ))}
      {page.payments_due.map((p) => (
        <div
          key={`due-${p.added_line_ref ?? "order"}`}
          className="flex flex-wrap items-center justify-between gap-2"
        >
          <p className="text-sm">
            <span className="font-medium tabular-nums">
              {formatQuotedPrice(p.amount_pence)}
            </span>{" "}
            to pay by card
            {p.for_sentence && (
              <span className="text-muted-foreground">
                {" "}
                for {p.for_sentence}
              </span>
            )}
          </p>
          {actions && (
            <PayButton addedLineRef={p.added_line_ref} pay={actions.pay} />
          )}
        </div>
      ))}
      {page.proformas_due.map((proforma) => (
        <div key={proforma.proforma_no} className="space-y-2">
          <ProformaPayment
            proforma={proforma}
            bank={page.bank_account}
            fetchFile={actions?.fetchFile ?? null}
          />
          {proforma.pay_by_card_url && actions && (
            <PayButton
              addedLineRef={proforma.added_line_ref}
              pay={actions.pay}
              label="Pay by card instead"
            />
          )}
        </div>
      ))}
      {hasCardDue && page.proforma_refused_sentence && (
        <p className="text-xs text-muted-foreground">
          Bank transfer (pro-forma): {page.proforma_refused_sentence}
        </p>
      )}
      {page.po_number_owed && actions && (
        <OrderPoNumber
          formatHint={poFormatHint(page.po_number_owed)}
          givePoNumber={actions.givePoNumber}
          reload={actions.reload}
        />
      )}
      <p className="text-xs text-muted-foreground">{page.payment_sentence}</p>
    </div>
  );
}

/** "£495.60 to pay" / "paid" / "on your account", as the read words it, with a capital. */
function moneyStateText(page: OrderPageView): string {
  return page.money_state.charAt(0).toUpperCase() + page.money_state.slice(1);
}

function needsRows(
  page: OrderPageView,
  actions: OrderPageActions | null,
  openItemAt: (row: OrderNeedView) => void,
  openProof: (row: OrderNeedView) => void,
  goToMoney: () => void,
  proofSlot: PortalPageSlots["orderProof"],
): NeedsYouItem[] {
  return page.needs.map((row) => {
    const base = { key: row.key, title: row.title, why: row.why };
    switch (row.kind) {
      // Paying is done in the summary beside the page, and the PO number is given in its one field there: these
      // rows say what is owed, as they always did, and their button takes the customer to it. (Otherwise Pay now and
      // a pro-forma's bank details would be on the page twice.)
      case "pay": {
        const proforma = row.proforma_no
          ? page.proformas_due.find((p) => p.proforma_no === row.proforma_no)
          : undefined;
        return {
          ...base,
          icon: "pay",
          action: actions ? (
            <Button size="sm" variant="outline" onClick={() => goToMoney()}>
              {proforma ? "How to pay ›" : "Pay ›"}
            </Button>
          ) : undefined,
        };
      }
      case "po":
        return {
          ...base,
          icon: "po",
          action: actions ? (
            <Button size="sm" variant="outline" onClick={() => goToMoney()}>
              Add PO ›
            </Button>
          ) : undefined,
        };
      case "proof":
        return {
          ...base,
          icon: "proof",
          below: row.proof_id ? proofSlot?.(row.proof_id) : undefined,
          action: (
            <Button
              size="sm"
              variant="outline"
              onClick={() => row.proof_id && openProof(row)}
            >
              Review ›
            </Button>
          ),
        };
      default:
        return {
          ...base,
          icon:
            row.kind === "name"
              ? "name"
              : row.is_cant_print
                ? "cant_print"
                : "upload",
          action: actions ? (
            <Button size="sm" variant="outline" onClick={() => openItemAt(row)}>
              {row.kind === "name"
                ? "Name it ›"
                : row.kind === "tag_pages"
                  ? "Finish ›"
                  : "Upload ›"}
            </Button>
          ) : undefined,
        };
    }
  });
}

/** The Price tab (Ben, 5 Oct): the breakdown, service lines included, with
 *  one quiet status line under it. Anything the customer must still do
 *  (pay, give a PO number) lives in Needs you. */
function PriceTab({
  page,
  pay,
}: {
  page: OrderPageView;
  pay: OrderPageActions["pay"] | null;
}) {
  const parts: React.ReactNode[] = [];
  for (const p of page.card_payments) {
    parts.push(
      <span key={`paid-${p.paid_at}`}>
        Paid {formatQuotedPrice(p.amount_pence)} by card on{" "}
        {formatWeekdayDateOrDash(p.paid_at)}
        {p.for_sentence && ` for ${p.for_sentence}`}
      </span>,
    );
  }
  for (const p of page.payments_due) {
    parts.push(
      <span key={`due-${p.added_line_ref ?? "order"}`}>
        {formatQuotedPrice(p.amount_pence)} to pay by card
        {p.for_sentence && ` for ${p.for_sentence}`}
        {!page.is_closed && pay && (
          <>
            {" · "}
            <button
              type="button"
              className="text-sm text-primary hover:underline"
              onClick={() => void pay(p.added_line_ref)}
            >
              Pay now
            </button>
          </>
        )}
      </span>,
    );
  }
  for (const p of page.proformas_due) {
    parts.push(
      <span key={`proforma-${p.proforma_no}`}>
        {formatQuotedPrice(p.outstanding_pence)} to pay against pro-forma{" "}
        {p.proforma_no}
      </span>,
    );
  }
  const receipt = page.documents.find((d) => d.kind === "receipt");
  if (receipt) {
    parts.push(
      <span key="receipt">Paid: your receipt is {receipt.document_no}</span>,
    );
  }
  if (
    page.card_payments.length === 0 &&
    page.payments_due.length === 0 &&
    page.proformas_due.length === 0 &&
    !receipt &&
    !page.is_cancelled
  ) {
    parts.push(<span key="account">On your account: we invoice you</span>);
  }
  if (page.customer_order_number) {
    parts.push(<span key="po">Your PO: {page.customer_order_number}</span>);
  }
  return (
    <Card className="gap-3 p-5">
      <PriceBreakdown
        rows={page.lines.map((l) => ({
          ref: l.ref,
          title:
            l.kind === "service" && l.spec ? `${l.title}, ${l.spec}` : l.title,
          quantity: l.kind === "service" ? null : l.quantity,
          price_pence: l.price_pence,
          vat_label: l.vat_label,
          is_not_yet_priced: l.price_pence === null,
        }))}
        cancelledRows={page.cancelled_lines}
        totalPence={page.total_pence}
        totalIncVatPence={page.total_inc_vat_pence}
      />
      {parts.length > 0 && (
        <p className="flex flex-wrap gap-x-1.5 gap-y-1 border-t pt-3 text-sm text-muted-foreground">
          {parts.map((part, i) => (
            <span key={i}>
              {i > 0 && <span aria-hidden="true">· </span>}
              {part}
            </span>
          ))}
        </p>
      )}
    </Card>
  );
}

export function OrderPageBody({
  page,
  actions,
  arrival,
  slot,
  backLink,
  loadKitDrops = null,
}: {
  page: OrderPageView;
  /** Reads a kit line's places a page at a time (the door's own credential behind it); null: the sentence alone. */
  loadKitDrops?: LoadKitDrops | null;
  /** Null: a page that acts as nobody (the staff view-as mirror): nothing to pay, send, upload, approve or download. */
  actions: OrderPageActions | null;
  arrival: OrderArrival;
  /** A block a shell may place above the tabs. The body renders whatever it is handed and knows nothing of what or why:
   *  the customer's pages are given none. */
  slot?: ReactNode;
  /** The shell's way back (to its Orders list), set above the order number in the page's header. */
  backLink?: ReactNode;
}) {
  const { searchParams, setParams, selectItem } = useItemPageParams();
  const slots = usePortalPageSlots();
  // The tab a link with none lands on is fixed when the order loads, so
  // finishing the last action doesn't move the customer off Needs you.
  // Back from the card payment it stays on Needs you even with nothing left, which then says so.
  const [landingTab] = useState<PageTab>(() =>
    resolvePageTab(null, arrival === "payment_return" ? 1 : page.needs.length),
  );
  // The Needs you row the customer opened an item from. Once that row clears (the proof decided, the file in,
  // the version named) the page takes them back to Needs you rather than leaving them on the item, and focus
  // goes to the next thing that needs them.
  const [openedFromNeed, setOpenedFromNeed] = useState<string | null>(null);
  const [needsFocusAt, setNeedsFocusAt] = useState<number | null>(null);
  const needsPanelRef = useRef<HTMLDivElement>(null);

  const items = page.lines.filter((l) => l.kind === "item");
  const itemOfProof = (id: string) =>
    items.find((l) =>
      (l.artwork ?? []).some((v) => v.proofs.some((p) => p.id === id)),
    )?.ref ?? null;

  // A link with only `?proof=` (the proof page's way back, an emailed deep link) opens that proof: its tab and
  // item follow from it. An id that is not on this page is ignored, so the page opens as it would without it.
  const proofId = searchParams.get("proof");
  const proofItem = proofId ? itemOfProof(proofId) : null;
  const requestedTab = searchParams.get("tab");
  const tab = requestedTab
    ? resolvePageTab(requestedTab, 0)
    : proofItem
      ? "items"
      : landingTab;
  const itemRef = searchParams.get("item") ?? proofItem;
  const focus = searchParams.get("focus");
  const pane = searchParams.get("view") as ItemPane | null;
  const openProof = (id: string) =>
    setParams({
      tab: "items",
      item: itemOfProof(id),
      view: "artwork",
      proof: id,
      focus: null,
    });
  const openItemAt = (row: OrderNeedView) =>
    setParams({
      tab: "items",
      item: row.ref,
      view: "artwork",
      proof: null,
      focus: row.version_id,
    });
  const itemActions = {
    writes: actions?.writes ?? READ_ONLY_WRITES,
    fetchFile: actions?.fetchFile ?? NO_FILES,
    reload: actions?.reload ?? (() => Promise.resolve()),
    openProof,
    closeProof: () => setParams({ proof: null, view: "artwork" }),
    openDelivery: () =>
      setParams({ tab: "delivery", item: null, view: null, proof: null }),
  };
  const hasAttention = (ref: string) => page.needs.some((n) => n.ref === ref);
  const needKeys = page.needs.map((n) => n.key).join("|");
  const previousNeedKeys = useRef(needKeys === "" ? [] : needKeys.split("|"));
  const defaultItem =
    items.find((l) => hasAttention(l.ref))?.ref ?? items[0]?.ref ?? null;

  // A Needs you row clearing: back to Needs you if the customer had opened an item from it, and focus on the
  // row that now sits where it was (the next thing needing them), or on "nothing left" when none does.
  useEffect(() => {
    const before = previousNeedKeys.current;
    const now = needKeys === "" ? [] : needKeys.split("|");
    previousNeedKeys.current = now;
    const isReturning =
      openedFromNeed !== null && !now.includes(openedFromNeed);
    const clearedAt = isReturning
      ? Math.max(before.indexOf(openedFromNeed), 0)
      : tab === "needs"
        ? before.findIndex((k) => !now.includes(k))
        : -1;
    if (isReturning) {
      setOpenedFromNeed(null);
      setParams({
        tab: "needs",
        item: null,
        proof: null,
        focus: null,
        view: null,
      });
    }
    if (clearedAt >= 0) setNeedsFocusAt(clearedAt);
    // `setParams` is rebuilt every render and only writes the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needKeys, openedFromNeed, tab]);

  useEffect(() => {
    if (needsFocusAt === null || tab !== "needs") return;
    setNeedsFocusAt(null);
    const panel = needsPanelRef.current;
    if (!panel) return;
    const rows = panel.querySelectorAll<HTMLElement>("[data-needs-row='open']");
    const row = rows[Math.min(needsFocusAt, rows.length - 1)];
    const target = row
      ? (row.querySelector<HTMLElement>("button, input, a[href]") ?? row)
      : panel.querySelector<HTMLElement>("[data-needs-all-done]");
    target?.focus();
  }, [needsFocusAt, tab, page.needs]);

  // The money summary beside the page (a bar and sheet on a phone), as the quote page's decision area is. A
  // cancelled order has nothing to pay or total, so it has no summary and keeps the narrow column.
  const hasSummary = !page.is_cancelled;
  const isSummaryBesidePage = useMediaQuery(SUMMARY_BESIDE_PAGE);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const summaryPanelRef = useRef<HTMLElement>(null);
  // The PO number's Needs you row takes the customer to the one PO field: in the summary's panel on a wide screen
  // (it takes focus), in its sheet on a phone.
  const goToMoney = () => {
    if (!isSummaryBesidePage) {
      setIsSheetOpen(true);
      return;
    }
    const panel = summaryPanelRef.current;
    panel?.scrollIntoView({ block: "nearest" });
    (panel?.querySelector<HTMLElement>("input") ?? panel)?.focus();
  };
  const hasMoneyToDo =
    page.payments_due.length > 0 ||
    page.proformas_due.length > 0 ||
    page.po_number_owed !== null;
  const soleCardPayment =
    actions &&
    page.payments_due.length === 1 &&
    page.proformas_due.length === 0 &&
    page.po_number_owed === null
      ? page.payments_due[0]
      : null;
  const summaryProps = {
    label: "Your total and paying",
    sheetTitle: "Your total and paying",
    totalPence: page.total_pence,
    totalIncVatPence: page.total_inc_vat_pence,
    // The read's totals leave out a delivery not yet priced; say so beside them.
    totalsNote: page.lines.some((l) => l.price_pence === null)
      ? DELIVERY_NOT_YET_PRICED
      : null,
    state: {
      text: moneyStateText(page),
      isOwed: page.is_money_owed,
    },
    onOpenPrice: () => setParams({ tab: "price", item: null, view: null }),
    children: <MoneyActions page={page} actions={actions} />,
  };

  return (
    <SummaryPageFrame
      hasSummary={hasSummary}
      isSummaryBesidePage={isSummaryBesidePage}
      header={
        <>
          {backLink}
          <div className="space-y-2">
            <p className="font-mono text-xs text-muted-foreground">
              {page.order_no}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                {page.title}
              </h1>
              {page.is_cancelled ? (
                <Badge variant="neutral">Cancelled</Badge>
              ) : (
                page.is_closed && <Badge variant="success">Complete</Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>
                For{" "}
                <span className="font-medium text-foreground">
                  {page.customer_name}
                </span>
              </span>
              {page.placed_by && <span>Placed by {page.placed_by}</span>}
              {page.total_inc_vat_pence !== null && !page.is_cancelled && (
                <span>
                  {formatQuotedPrice(page.total_inc_vat_pence)} inc VAT ·{" "}
                  <span
                    className={cn(
                      "font-medium",
                      page.is_money_owed
                        ? "text-amber-800 dark:text-amber-300"
                        : "text-foreground",
                    )}
                  >
                    {page.money_state}
                  </span>
                </span>
              )}
              <span>Ordered {formatWeekdayDateOrDash(page.ordered_on)}</span>
              {page.required_on && (
                <span>
                  Needed by {formatWeekdayDateOrDash(page.required_on)}
                </span>
              )}
              <span>
                {page.customer_order_number
                  ? `Your PO ${page.customer_order_number}`
                  : page.po_number_owed
                    ? "PO number to follow"
                    : page.po_note
                      ? `PO number: ${page.po_note.toLowerCase()}`
                      : "No PO number"}
              </span>
            </div>
            {page.date_sentence && !page.is_closed && (
              <p
                className={cn(
                  "inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium",
                  page.approve_by_tier === "passed"
                    ? "bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-200"
                    : page.approve_by_tier === "tight"
                      ? "bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
                      : "bg-muted/60",
                )}
              >
                <CalendarClock className="size-4" />
                {page.date_sentence}
              </p>
            )}
          </div>

          {arrival === "placed" && (
            <Card className="flex-row items-center gap-3 border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/20">
              <CheckCircle2 className="size-5 shrink-0 text-green-700 dark:text-green-400" />
              <p className="text-sm font-medium">
                Thank you. We&apos;ve got your order.
              </p>
            </Card>
          )}
          {arrival === "payment_return" && (
            <Card className="flex-row items-center gap-3 border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/20">
              <CheckCircle2 className="size-5 shrink-0 text-green-700 dark:text-green-400" />
              <p className="text-sm font-medium">
                {page.payments_due.length === 0
                  ? "Thank you. Your payment has gone through."
                  : "Thank you. We're confirming your payment."}
              </p>
            </Card>
          )}
          {page.is_closed && page.closed_sentence && (
            <Card className="flex-row items-center gap-3 p-4">
              <PackageCheck className="size-5 shrink-0 text-green-700 dark:text-green-400" />
              <p className="text-sm font-medium">{page.closed_sentence}</p>
            </Card>
          )}

          {slot}
        </>
      }
      panel={<OrderSummaryPanel {...summaryProps} panelRef={summaryPanelRef} />}
      bar={
        <OrderSummaryBar
          {...summaryProps}
          isSheetOpen={isSheetOpen}
          onSheetOpenChange={setIsSheetOpen}
          barAction={
            soleCardPayment
              ? {
                  label: "Pay now ›",
                  onPress: () =>
                    void actions
                      ?.pay(soleCardPayment.added_line_ref)
                      .catch(() => setIsSheetOpen(true)),
                }
              : hasMoneyToDo
                ? { label: "Details ›", onPress: null }
                : null
          }
        />
      }
    >
      <PageTabs
        label="Order"
        current={tab}
        needsCount={page.needs.length}
        itemsCount={items.length}
        itemsNeedAttention={items.some((l) => hasAttention(l.ref))}
        onSelect={(t: PageTab) => {
          setOpenedFromNeed(null);
          setParams({
            tab: t,
            item: null,
            proof: null,
            focus: null,
            view: null,
          });
        }}
      />

      <div role="tabpanel">
        {tab === "needs" && (
          <div className="space-y-4" ref={needsPanelRef}>
            <NeedsYouList
              rows={needsRows(
                page,
                actions,
                (row) => {
                  setOpenedFromNeed(row.key);
                  openItemAt(row);
                },
                (row) => {
                  setOpenedFromNeed(row.key);
                  if (row.proof_id) openProof(row.proof_id);
                },
                goToMoney,
                slots.orderProof,
              )}
              emptySentence="Nothing needed from you right now."
            />
            {page.proofs_intro &&
              page.needs.some((n) => n.kind === "proof") && (
                <p className="text-sm text-muted-foreground">
                  {page.proofs_intro}
                </p>
              )}
            {page.meanwhile.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-sm font-medium">Meanwhile</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {page.meanwhile.map((m) => (
                    <li key={m.ref}>
                      <button
                        type="button"
                        className="text-left text-sm hover:text-foreground"
                        onClick={() =>
                          setParams({ tab: "items", item: m.ref, view: null })
                        }
                      >
                        <span className="tabular-nums">{m.ref}</span> {m.title}:{" "}
                        {m.sentence}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {tab === "items" && (
          <div className="space-y-3">
            {page.proofs_intro && (
              <p className="text-sm text-muted-foreground">
                {page.proofs_intro}
              </p>
            )}
            <ItemsListDetail
              cancelled={page.cancelled_lines.filter((l) => l.kind === "item")}
              items={items.map((l) => ({
                key: l.ref,
                ref: l.ref,
                title: l.title,
                status: l.list_status,
                hasAttention: hasAttention(l.ref),
              }))}
              selectedKey={itemRef}
              defaultKey={defaultItem}
              onSelect={(key) =>
                selectItem(key, { proof: null, focus: null, view: null })
              }
              backLabel="All items"
              renderDetail={(key) => {
                const line = items.find((l) => l.ref === key);
                return line ? (
                  <OrderItemDetail
                    key={key}
                    page={page}
                    line={line}
                    proofId={proofId}
                    focusVersionId={focus}
                    pane={pane}
                    onPane={(p) =>
                      setParams({ view: p, proof: null, focus: null })
                    }
                    actions={itemActions}
                  />
                ) : null;
              }}
            />
          </div>
        )}
        {tab === "delivery" && (
          <DeliveryByAddress
            groups={page.delivery_groups}
            loadKitDrops={loadKitDrops}
            kits={page.lines.flatMap((l) =>
              l.kit
                ? [
                    {
                      ref: l.ref,
                      title: l.title,
                      summary: l.kit.summary,
                      dropsUrl: l.kit.drops_url,
                    },
                  ]
                : [],
            )}
            sentence={page.is_closed ? null : page.date_sentence}
            charges={page.lines
              .filter((l) => l.is_delivery_charge)
              .map((l) => ({
                ref: l.ref,
                title: l.title,
                spec: l.spec,
                price_pence: l.price_pence,
                vat_label: l.vat_label,
              }))}
          />
        )}
        {tab === "price" && <PriceTab page={page} pay={actions?.pay ?? null} />}
        {tab === "documents" && (
          <DocumentsList
            documents={page.documents.map((d) => ({
              key: d.document_no,
              kind: d.kind_label,
              // A pro-forma says where it stands, a credit note what it credits.
              label:
                d.status_label || d.detail
                  ? `${d.document_no} · ${d.status_label ?? d.detail}`
                  : d.document_no,
              dated: d.dated,
              url: actions ? d.url : null,
            }))}
            emptySentence={page.documents_empty_sentence}
            fetchFile={actions?.fetchFile ?? null}
          />
        )}
        {tab === "notes" && (
          <NotesPanel
            messages={[]}
            questionsHeading="Questions about this order?"
            contact={page.contact}
            contactLine={null}
          />
        )}
      </div>
    </SummaryPageFrame>
  );
}
